import { Dispatch } from "react-redux";
import { ActionTypes, IUpdateOptnSrtrData } from ".";
import { AuthActionCreators, IApplicationState } from "../";
import {
    IAnatomyPrefsData,
    IBaseResponse,
    ICenterAddress, ICenterUnosDataMonthly, IDocumentsVersion, IInsuranceCarrier, ILabData, IListItem, IOpo, IProfileData, ISerologyData, ISerologyTest, IServicesData, ISrtrData, IContact, IUnosData,
    IUpdateAdminDataRequest, IUpdateAnatomyPrefsDataRequest, IUpdateCenterProfileRequest, IUpdateLabDataRequest,
    IUpdateSerologyDataRequest, IUpdateServicesDataRequest, IUpdateSrtrDataRequest, IVersionedFile, IManualInvoiceLineItemTax, IOpoForCenterProfile
} from "../../models/index";
import { IGet, IPost } from '../authorization';

export interface IUpdateUnosDataResponse {
    data: {
        success: boolean;
        message: string;
        results: IUnosData[];
    }
}

export interface ICenterProfileLookupData {
    opoList: IOpoForCenterProfile[],
    requiredTubeList: IListItem[],
    anatomyMaxPreferenceList: IListItem[],
    anatomyMaxPreferenceRightList: IListItem[],
    anatomyMaxPreferenceLeftList: IListItem[],
    pairWorkupList: IListItem[],
    workupWaitList: IListItem[],
    xmVirtualOptionList: IListItem[],
    insuranceCarrierList: IInsuranceCarrier[],
    serologyTestList: ISerologyTest[],
    documentVersionList: IDocumentsVersion[],
    staffList: IContact[],
    micrositeCoachList: IListItem[],
    centerAddressList: ICenterAddress[],
    coordinatorList: IListItem[]
}

export const initialCenterProfileLookupData: ICenterProfileLookupData = {
    opoList: [],
    requiredTubeList: [],
    anatomyMaxPreferenceList: [],
    anatomyMaxPreferenceRightList: [],
    anatomyMaxPreferenceLeftList: [],
    pairWorkupList: [],
    workupWaitList: [],
    xmVirtualOptionList: [],
    insuranceCarrierList: [],
    serologyTestList: [],
    documentVersionList: [],
    staffList: [],
    micrositeCoachList: [],
    centerAddressList: [],
    coordinatorList: []
};

export interface ICenterProfileLookupDataResponse {
    data: {
        success: boolean;
        message: string;
        results: ICenterProfileLookupData
    }
}

export interface ICenterAdminDataResponse {
    data: {
        success: boolean;
        message: string;
        results: any
    }
}

export interface ICenterUnosDataResponse {
    data: {
        success: boolean;
        message: string;
        results: ICenterUnosDataMonthly[];
    }
}

export interface ICenterProfileResponse {
    data: {
        success: boolean,
        message: string,
        results: IProfileData
    }
}

export interface ICenterSrtrDataResponse {
    data: {
        success: boolean,
        message: string,
        results: ISrtrData
    }
}

export interface ICenterLabDataResponse {
    data: {
        success: boolean,
        message: string,
        results: ILabData
    }
}

export interface ICenterServicesDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IServicesData
    }
}

export interface ICenterSerologyDataResponse {
    data: {
        success: boolean,
        message: string,
        results: ISerologyData
    }
}

export interface ICenterAnatomyPrefsDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IAnatomyPrefsData
    }
}

export interface ICenterLegalDocumentsResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            streamlinedFiles: IVersionedFile[],
            notStreamlinedFiles: IVersionedFile[],
            documentVersions: IDocumentsVersion[],
            isStreamlinedContract: boolean,
            invoicePoNumber: string,
            invoicePoExpirationDate: string
        }
    }
}

export interface IManualInvoiceLineItemLookupResponse {
    data: {
        success: boolean;
        message: string;
        results: {
            lineItemTypeList: IListItem[]
        }
    }
}

export interface IManualInvoiceLineItemTaxResponse {
    data: {
        success: boolean;
        message: string;
        results: IManualInvoiceLineItemTax[]
    }
}

export const enum OrganPackaging {
    InHouse = 1,
    Opo = 2
};

export interface ICenterPackagingDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            organPackaging: OrganPackaging,
            achRoutingNumber: string,
            achAccountNumber: string,
            selectedOpo: number
        }
    }
}

export const ActionCreatorsCenterProfile = {
    getCenterProfileLookupData: (centerId: number, organType?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterProfileLookupDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/profile-lookup-data${organType != null ? `?organType=${organType}` : ''}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getAdminData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAdminDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/admin-data`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateAdminData: (centerId: number, data: IUpdateAdminDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAdminDataResponse> => {
        let formData = new FormData();

        let fields = Object.entries(data);

        for (const [key, value] of fields.filter(([k, v]) => k != 'files')) {
            formData.append(key, value);
        }

        for (let i = 0; i < data.files.length; i++) {
            formData.append(`files[${i}].FileType`, data.files[i].fileType.toString());
            formData.append(`files[${i}].FileVersion`, data.files[i].fileVersion.toString());
            formData.append(`files[${i}].FileDate`, data.files[i].fileDate);
            formData.append(`files[${i}].File`, data.files[i].file);
        }

        let options = {
            url: `${__API__}/center/${centerId}/admin-data`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getSrtrData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterSrtrDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/srtr-data`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateSrtrData: (data: IUpdateSrtrDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterSrtrDataResponse> => {
        let formData = new FormData();

        let fields = [
            'id',
            'dcAdultGraftSurvival1',
            'dcAdultGraftSurvival3',
            'dcPediatricGraftSurvival1',
            'dcPediatricGraftSurvival3',
            'adultGraftSurvival1',
            'adultGraftSurvival3',
            'pediatricGraftSurvival1',
            'pediatricGraftSurvival3',
            'dcAdultPatientSurvival1',
            'dcAdultPatientSurvival3',
            'dcPediatricPatientSurvival1',
            'dcPediatricPatientSurvival3',
            'srtrSourceDate',
        ];

        for (let i = 0; i < fields.length; i++) {
            formData.append(fields[i], data[fields[i]]);
        }

        let files = [
            'graftSurvival',
            'srtrPatientSurvival'
        ];

        for (let i = 0; i < files.length; i++) {
            formData.append(files[i] + 'File', data[files[i] + 'File']);
            formData.append(files[i] + 'FileVersion', data[files[i] + 'FileVersion']);
            formData.append(files[i] + 'FileDate', data[files[i] + 'FileDate']);
        }

        let options = {
            url: `${__API__}/center/${data.id}/srtr-data`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },

    getCenterUnosData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterUnosDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/unos-data`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateCenterUnosData: (centerId: number, data: IUpdateOptnSrtrData[]) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterUnosDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/unos-data`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },


    getLineItemLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IManualInvoiceLineItemLookupResponse> => {
        let options = {
            url: `${__API__}/center/profile/lookup`
        };
        return await dispatch(AuthActionCreators.apiGet(options));
    },


    getLineItems: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IManualInvoiceLineItemTaxResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/profile/edit/tax`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IManualInvoiceLineItemTaxResponse) => {
            return Promise.resolve(response);
        });

    },

    addLineItem: (lineItemType: number, centerId: number, taxPercent: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/center/profile/edit/tax`,
            data: {
                lineItemType,
                centerId,
                taxPercent
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    removeLineItem: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/center/profile/edit/tax/${id}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    getCenterProfile: (centerId: number, organType?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterProfileResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/center-profile${organType != null ? `?organType=${organType}` : ''}`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updateCenterProfile: (data: IUpdateCenterProfileRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterProfileResponse> => {
        let formData = new FormData();

        let fields = [
            'id', 'webUrl', 'name', 'opoId', 'earliestOrganPickupTime', 'latestOrganPickupTime',
            'timezone',
            'avgCostLDEvalWorkup', 'doesOpoPackorPerf', 'opoCostPackAndPerf',
            'achRoutingNumber', 'achAccountNumber', 'invoiceDaysToRemit', 'isTaxExempt', 'taxExemptExpirationDate',
            'streamlinedHospitalBilling', 'physicianDonorNephrectomyCharges', 'provideSwapChecklists', 'canUtilizeA2toBPatients',
            'leadCoordinatorId', 'organType'
        ];

        for (let i = 0; i < fields.length; i++) {
            formData.append(fields[i], data[fields[i]]);
        }

        let files = [
            'w9',
            'cmsLetter',
            'salesTaxExemptCert'
        ];

        for (let i = 0; i < files.length; i++) {
            formData.append(files[i], data[files[i]]);

            if (i == 2 && data.salesTaxExemptCert.file) {
                formData.append(`salesTaxExemptCert.FileType`, data.salesTaxExemptCert.fileType.toString());
                formData.append(`salesTaxExemptCert.FileVersion`, data.salesTaxExemptCert.fileVersion.toString());
                formData.append(`salesTaxExemptCert.FileDate`, data.salesTaxExemptCert.fileDate);
                formData.append(`salesTaxExemptCert.File`, data.salesTaxExemptCert.file);
            }
        }

        let options = {
            url: `${__API__}/center/${data.id}/center-profile`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getLabData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterLabDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/lab-data`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updateLabData: (data: IUpdateLabDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterLabDataResponse> => {
        let options = {
            url: `${__API__}/center/${data.id}/lab-data`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getServicesData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterServicesDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/services-data`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updateServicesData: (data: IUpdateServicesDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterServicesDataResponse> => {
        let options = {
            url: `${__API__}/center/${data.id}/services-data`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getSerologyData: (centerId: number, organType: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterSerologyDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/serology-data?organType=${organType}`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updateSerologyData: (data: IUpdateSerologyDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterSerologyDataResponse> => {
        let options = {
            url: `${__API__}/center/${data.id}/serology-data`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getAnatomyPrefsData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAnatomyPrefsDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/anatomy-prefs-data`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updateAnatomyPrefsData: (data: IUpdateAnatomyPrefsDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAnatomyPrefsDataResponse> => {
        let options = {
            url: `${__API__}/center/${data.id}/anatomy-prefs-data`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getLegalDocuments: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterLegalDocumentsResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/legal-documents`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },

    setAddAddress: (addAddress: boolean) => {
        return {
            type: ActionTypes.SET_ADD_ADDRESS,
            addAddress
        };
    },

    getVersionedFile: (centerId: number, id: number) => (dispatch: Dispatch<IApplicationState>) => {
        var options: IGet = {
            url: __API__ + `/center/${centerId}/versioned-file/${id}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    uploadVersionedFile: (id: number, versionedFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('versionedFile', versionedFile);

        var config: IPost = {
            url: `${__API__}/center/${id}/upload-versioned-file/`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },

    getCmsFile: (centerId: number) => (dispatch: Dispatch<IApplicationState>) => {
        var options: IGet = {
            url: __API__ + `/center/${centerId}/cms-file`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getW9File: (centerId: number) => (dispatch: Dispatch<IApplicationState>) => {
        var options: IGet = {
            url: __API__ + `/center/${centerId}/w9-file`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getPackagingData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterPackagingDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/packaging-data`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
    updatePackagingData: (centerId: number, organPackaging: OrganPackaging, achRoutingNumber: string, achAccountNumber: string, selectedOpo: number, achUpdated: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterPackagingDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/packaging-data`,
            data: {
                organPackaging,
                achRoutingNumber,
                achAccountNumber,
                selectedOpo,
                achUpdated
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
};
